import { Mui, MuiIcons } from "@osu/react-ui";
import React, { Fragment, useEffect, useRef, useState } from "react";
import useStyles from "../styles/History";
import { ACTION_STATUS, APPROVAL_STATUS, FORM_HISTORY_STEP, FORM_HISTORY_LABEL, MAX_LENGTH_EMPLID, TASK_ACTION, TASK_ACTION_TRANSFER } from "../../util/constants";
import { formatValue, isValidEmplidLength, transformReportStatus } from "../../util/functions";

const TABLE_COLLAPSE_MAX_WIDTH = "763px";
const styleProps = { tableCollapseMaxWidth: TABLE_COLLAPSE_MAX_WIDTH };

function History(params) {
  const { completeTask, resendTask, getHistory, getPerson, emplid, formId, formType, columns, history, isAdmin, person, personStatus, resetPerson,
    status, error, setErrorMessage, resetTaskAction, taskActionMessage, taskActionStatus, transferTask } = params;
  const isPerformingTaskAction = (taskActionStatus === ACTION_STATUS.LOADING);
  const CLEAR_PENDING_STEPS = [FORM_HISTORY_STEP.GRAD_SCHOOL_REVIEW, FORM_HISTORY_STEP.COMPLETION];
  const styles = useStyles(styleProps);

  useEffect(() => {
    !status && formId && getHistory({ emplid, formType, id: formId });
  }, [emplid, formId, formType, getHistory, status])

  useEffect(() => {
    setErrorMessage(existing => {
      if(!error && existing) {
        return ""
      } else if (error) {
        return error
      }
    })
  }, [error, setErrorMessage])

  // when the task action completes, get updated history
  useEffect(() => {
    if(taskActionStatus === ACTION_STATUS.SUCCESS) getHistory({ emplid, formType, id: formId })
  }, [emplid, formId, formType, getHistory, taskActionStatus]);

  // when the task action fails, set error message
  useEffect(() => {
    if(taskActionStatus === ACTION_STATUS.ERROR) setErrorMessage((taskActionMessage ?? "Failed to perform Task Action."));
  }, [setErrorMessage, taskActionMessage, taskActionStatus]);

  // on unmount, reset state
  useEffect(() => {
    return () => {
      resetTaskAction();
      setErrorMessage("");
    };
  }, [resetTaskAction, setErrorMessage]);

  if(status === ACTION_STATUS.LOADING) {
    const columnWidths = ["25%", "20%", "15%", "25%", "15%"];
    const TableCells = columnWidths.map((columnWidth, index) => {
      return (<Mui.TableCell key={index} width={columnWidth}><Mui.Skeleton /></Mui.TableCell>);
    });
    return (
      <div>
        <div aria-live="assertive" className="sr-only">Loading History, please wait...</div>
        <Mui.Stepper orientation="vertical">
          {[...Array(1)].map((item, index) => (
            <Mui.Step active key={`step-${index}`}>
              <Mui.StepLabel StepIconComponent={(props) => null}><Mui.Skeleton width="20%" /></Mui.StepLabel>
              <Mui.StepContent>
                <Mui.Table>
                  <Mui.TableHead><Mui.TableRow>{TableCells}</Mui.TableRow></Mui.TableHead>
                  <Mui.TableBody><Mui.TableRow>{TableCells}</Mui.TableRow></Mui.TableBody>
                </Mui.Table>
              </Mui.StepContent>
            </Mui.Step>
          ))}
        </Mui.Stepper>
      </div>
    );
  }

  if(status === ACTION_STATUS.SUCCESS && history.length === 0) {
    return (
      <Mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
         <Mui.Typography component="span" variant="subtitle2" aria-live="assertive">No History</Mui.Typography>
      </Mui.Box>
    );
  }

  return (
    <div>
      {isPerformingTaskAction &&
          <Mui.Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
              <Mui.CircularProgress aria-describedby="performingTaskAction" />
              <Mui.Typography id="performingTaskAction" variant="body1" aria-live="assertive" >
                  Performing Task Action, please wait...
              </Mui.Typography>
          </Mui.Box>
      }
      <Mui.Stepper orientation="vertical" style={{...(isPerformingTaskAction ? { display: "none" } : {})}}>
        {history.map((record = {}, index) => {
          const { step, status, description = "", items: rows } = record;
          const label = (FORM_HISTORY_LABEL[step] ?? "");
          const key = `${encodeURIComponent(label)}-step-${index}`

          return (
            <Mui.Step active key={key}>
              <Mui.StepLabel StepIconComponent={(props) => <Icon {...props} status={status} />}>{label} {description}</Mui.StepLabel>
              <Mui.StepContent>
                <Mui.Table aria-label={`History - ${label} Table`} sx={styles.historyTable}>
                  <Mui.TableHead>
                    <Mui.TableRow>
                      {columns.map((col, index) => {
                        return <Mui.TableCell key={index} width={col.width} align="left">{col.label}</Mui.TableCell>
                      })}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {rows.map((row, rowIndex) => (
                      <Mui.TableRow key={rowIndex}>
                        {columns.map((col, colIndex) => {
                          let value = transformReportStatus(formType, row[col.dataField]);
                          if(col.dataField === "action" && value === APPROVAL_STATUS.PENDING && CLEAR_PENDING_STEPS.includes(step)) {
                            value = "";
                          }
                          return (
                            <Mui.TableCell key={colIndex} data-header={col.label} width={col.width} align="left">
                              {(col.dataField === "taskActions") ? 
                                (<TaskActions index={rowIndex} isAdmin={isAdmin} label={col.label} value={value} 
                                  getPerson={getPerson} person={person} personStatus={personStatus} resetPerson={resetPerson}
                                  complete={() => (completeTask({ emplid, formType, formId, approvalTask: row.task, approverEmplid: row.emplid }))} 
                                  resend={() => (resendTask({ emplid, formType, formId, approverEmplid: row.emplid, approverName: row.name, approverEmail: row.email }))}
                                  transfer={(transferApproverEmplid) => (transferTask({ emplid, formType, formId, taskName: row.task, approverEmplid: row.emplid, transferApproverEmplid }))}
                                />) :
                                formatValue(value)}
                            </Mui.TableCell>
                          );
                        })}
                      </Mui.TableRow>
                    ))}
                  </Mui.TableBody>
                </Mui.Table>
              </Mui.StepContent>
            </Mui.Step>
          );
        })}
      </Mui.Stepper>
    </div>
  );
}

History.defaultProps = {
  history: []
}

function Icon({active, completed, error, status, ...rest}) {
  if(status === "CANCELLED") {
    return (<MuiIcons.Cancel ariaLabel="Cancelled" color="error" />);
  } else if(status === "COMPLETE") {
    return <MuiIcons.CheckCircle {...rest} color="success" />
  } else if (status === "IN_PROGRESS") {
    return <MuiIcons.WatchLater color="secondary" />
  } else if (status === "DELAYED") {
    return <MuiIcons.Warning color="warning" />
  }
  return <MuiIcons.RadioButtonUnchecked />
}

function TaskActions(props) {
  const { complete, index = 0, isAdmin = false, getPerson, label = "Task Actions", person, personStatus, resend, resetPerson, transfer, value: taskActions = [] } = props;
  const retry = useRef(null);
  const transferButton = useRef(null);
  const [selectedTaskAction, setSelectedTaskAction] = useState("");
  const [transferApproverEmplid, setTransferApproverEmplid] = useState(null);

  // when getPerson returns an error, focus on the retry button
  useEffect(() => {
    if(personStatus === ACTION_STATUS.ERROR && retry.current) retry.current.focus();
  }, [personStatus]);

  // when getPerson succeeds, focus on the transfer button
  useEffect(() => {
    if(personStatus === ACTION_STATUS.SUCCESS && transferButton.current) transferButton.current.focus();
  }, [personStatus]);
  
  if(isAdmin && taskActions?.length > 0) {
    const id = `taskActionsSelect${index}`;
    const onChange = (e) => {
      const selectedValue = e.target.value
      setSelectedTaskAction(selectedValue);

      if(selectedValue !== "") {
        switch(TASK_ACTION[selectedValue]) {
          case TASK_ACTION.COMPLETE:
            complete();
            break;
          case TASK_ACTION.RESEND_EMAIL:
            resend();
            break;
          case TASK_ACTION.TRANSFER:
            break;
          default:
            console.log(`Change event not found for Task Action ${selectedValue}`);
        }
      }
    };
    const minColWidth = "7.2rem";
    const personResultsHeight = "2.75rem";
    const transferButtonEnabled = (person && person?.emplid);
    return (
      <Mui.Box marginY="0.5rem">
        <Mui.FormControl variant="outlined" fullWidth="true">
          <Mui.InputLabel htmlFor={id}>{label}</Mui.InputLabel>
          <Mui.Select native inputProps={{ id }} label={label} value={selectedTaskAction} onChange={onChange} sx={{ minWidth: minColWidth, maxWidth: "8.5rem" }}>
            <option aria-label="None" value="" />
            {taskActions.map((taskAction) => {
                const taskActionLabel = TASK_ACTION[taskAction];
                return (taskActionLabel ? <option key={taskAction} value={taskAction}>{taskActionLabel}</option> : null);
            })}
          </Mui.Select>
        </Mui.FormControl>
        {selectedTaskAction === TASK_ACTION_TRANSFER &&
          <Mui.Box marginTop="1rem">
            <Mui.Stack spacing={1}>
              <Mui.Box>
                <Mui.TextField label="Emplid" variant="outlined" value={transferApproverEmplid ?? ""} autoFocus={true} maxLength={MAX_LENGTH_EMPLID} sx={{ width: minColWidth }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if(isValidEmplidLength(value)) {
                        getPerson(value);
                    } else {
                        resetPerson();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/\D/g, ""); // remove non-digits
                    setTransferApproverEmplid(value.substring(0, MAX_LENGTH_EMPLID));
                  }}
                  onFocus={(e) => (resetPerson())}
                />
              </Mui.Box>
              <Mui.Box>
                {(personStatus === ACTION_STATUS.LOADING) &&
                  <Mui.Box display="flex" flexWrap="nowrap" alignItems="center" height={personResultsHeight}>
                      <Mui.CircularProgress size="20px" aria-describedby="personLoading" />
                      <Mui.Typography id="personLoading" variant="body1" aria-live="assertive" style={{ marginLeft: "0.5rem"}}>Finding Person...</Mui.Typography>
                  </Mui.Box>
                }
                {(personStatus === ACTION_STATUS.ERROR) &&
                  <Mui.Box display="flex" flexWrap="nowrap" alignItems="center" height={personResultsHeight}>
                      <Mui.Typography variant="body1" aria-live="assertive" style={{ marginRight: "0.5rem", verticalAlign: "middle"}}>Search Failed</Mui.Typography>
                      <Mui.IconButton ref={retry} color="primary" aria-label="Retry" onClick={() => getPerson(transferApproverEmplid)}><MuiIcons.Replay fontSize="small" /></Mui.IconButton>
                  </Mui.Box>
                }
                {(personStatus === ACTION_STATUS.NOT_FOUND) &&
                  <Mui.Box display="flex" alignItems="center" height={personResultsHeight}>
                    <Mui.Typography variant="body1" aria-live="assertive">Person not found.</Mui.Typography>
                  </Mui.Box>
                }
                {personStatus === ACTION_STATUS.SUCCESS &&
                  <Mui.Box display="flex" alignItems="center" height={personResultsHeight}>
                    <Mui.Typography variant="body1">{person.name}</Mui.Typography>
                  </Mui.Box>
                }
              </Mui.Box>
              <Mui.Box>
                <Mui.Button ref={transferButton} disabled={!transferButtonEnabled} color="primary" variant="contained"
                  onClick={() => {
                    if(transferButtonEnabled) transfer(transferApproverEmplid);
                  }}
                >Transfer</Mui.Button>
              </Mui.Box>
            </Mui.Stack>
          </Mui.Box>
        }
      </Mui.Box>
    );
  }
  return null;
}

export default History;
